import type {PropsWithChildren} from 'react';

import type {UnionProductConfiguration} from '@/modules/commerce/product/configuration';
import {
	getItemsForConfiguration,
	getProductDescriptionForConfiguration,
} from '@/modules/commerce/product/configuration';
import ProductBrandTags from '@/modules/commerce/product/ProductBrandTags';
import ProductImage from '@/modules/commerce/product/ProductImage';
import {Link} from '@/modules/foundation/components/link';
import {Heading} from '@/modules/foundation/components/typography/heading';
import type {CommerceBrand} from '@/sanity/schemas/types';

import {CustomizationIcon} from '../icons/customization-icon';
import {FormattedItemPrice} from '../item/FormattedItemPrice';

import ProductConfigPopover from './ProductConfigPopover';
import type {UnionProductFragment} from './query';

export type ProductCardBaseProps = {
	href: string;
	product: UnionProductFragment;
	headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
	configuration: UnionProductConfiguration;
	brands: CommerceBrand[];
	isConfigurable?: boolean;
};

export default function ProductCardBase({
	href,
	product,
	configuration,
	headingLevel = 2,
	brands,
	isConfigurable,
	children,
}: PropsWithChildren<ProductCardBaseProps>) {
	const productDescription = getProductDescriptionForConfiguration(product, configuration);
	const items = getItemsForConfiguration(product, configuration);

	return (
		<div className="flex h-full flex-col">
			<Link
				href={href}
				aria-label={product.name}
				className="bg-preview-background border-primary relative mb-2 block overflow-hidden rounded-sm border-b-2 p-4 lg:border-b-3"
			>
				<ProductImage
					product={product}
					config={configuration}
					className="flex-1"
					allowPlaceholders
				/>
			</Link>
			<div className="mb-1 flex grow flex-row items-start justify-between gap-4">
				<Heading
					level={headingLevel}
					size="h5"
					className="font-sans font-semibold text-balance"
				>
					{product.name}
				</Heading>
				<ProductConfigPopover description={productDescription} />
			</div>
			{children}
			{items.length && (
				<div className="text-primary font-display mb-3 text-4xl">
					<FormattedItemPrice items={items} />
				</div>
			)}
			<ProductBrandTags brands={brands}>
				{isConfigurable && (
					<div className="rounded-sm bg-black/70 px-2 py-1 text-neutral-200 lg:px-2.5 lg:py-1.5">
						<CustomizationIcon size={20} className="mr-1 inline-block" /> Kan tilpasses
					</div>
				)}
			</ProductBrandTags>
		</div>
	);
}
